import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Unlink, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/** Step before a clinician can delete: release every linked client (they become solo users; notes stay sealed). */
export function ReleaseClients() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/me/linked-clients").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!info || info.count === 0) return null;
  const release = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/me/release-clients", { password });
      toast.success(`${data.released} client${data.released === 1 ? "" : "s"} released. They've been told their account continues solo.`);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-3" data-testid="release-clients-card">
      <div className="flex items-start gap-2">
        <Unlink className="w-4 h-4 text-amber-800 mt-0.5 shrink-0" />
        <div className="text-sm text-stone-700">
          <strong data-testid="release-clients-count">{info.count} linked client{info.count === 1 ? "" : "s"}</strong> still point{info.count === 1 ? "s" : ""} at you: {info.clients.map((c) => c.name).join(", ")}.
          Releasing them turns their accounts into solo accounts — habits, garden and history stay with them, and they can connect with another coach later with that clinician's code.
          {info.notes > 0 && <> Your <strong>{info.notes} session note{info.notes === 1 ? "" : "s"}</strong> stay encrypted and sealed; no one can read them after this. <Link to="/clinician" className="inline-flex items-center gap-1 font-semibold text-amber-900 hover:underline" data-testid="release-export-hint"><FileText className="w-3.5 h-3.5" /> Export member summaries first</Link> if you need copies for your records.</>}
        </div>
      </div>
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="release-open" className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-amber-300 text-amber-900 hover:bg-amber-100">Release all people you support…</button>
      ) : (
        <form onSubmit={release} className="space-y-2" data-testid="release-form">
          <input data-testid="release-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password"
            className="w-full px-3 py-2 rounded-xl border border-amber-200 text-sm focus:border-amber-600 outline-none" />
          <div className="flex gap-2">
            <button data-testid="release-submit" type="submit" disabled={busy || !password} className="px-4 py-2 rounded-full text-sm font-semibold bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50">{busy ? "Releasing…" : `Release ${info.count} client${info.count === 1 ? "" : "s"}`}</button>
            <button type="button" data-testid="release-cancel" onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-semibold bg-white text-stone-700">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
